'use client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { Path } from '@pickleballinc/ui/components/team-leagues-movements/_components/path';
import { Step } from '@pickleballinc/ui/components/team-leagues-movements/_components/step';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { StepFour } from '@/components/molecules/StepFour';
import { StepOne } from '@/components/molecules/StepOne';
import { StepThree } from '@/components/molecules/StepThree';
import { StepTwo } from '@/components/molecules/StepTwo';
import { TeamHeader } from '@/components/molecules/TeamHeader';
import { TradePreview } from '@/components/molecules/TradePreview';
import { TeamLeagueTeamsInfo } from '@/entities/TeamLeagueTeamsInfo';
import { tradeTeamLeaguePlayersController } from '@/interface-adapters/controllers/players/trade-team-league-players.controller';
import { useOrganizationStore } from '@/store/organization-store';
import { TeamInfo } from '@/entities/TeamLeagueTeamInfo';
import { TeamPlayerInfo } from '@/lib/types';
import { useQueryClient } from '@tanstack/react-query';

export function TradePlayerContent({
	players,
	teams,
	currentTeam
}: {
	teams: TeamLeagueTeamsInfo[];
	players: TeamPlayerInfo[];
	currentTeam: TeamInfo;
}) {
	const store = useOrganizationStore();
	const router = useRouter();
	const params = useParams<{ teamLeagueId: string; teamId: string }>();

	const [step, setStep] = useState(1);
	const [currentTeamPlayers, setCurrentTeamPlayers] = useState(players);
	const [currentTeamDropped, setCurrentTeamDropped] = useState<TeamPlayerInfo[]>([]);
	const [otherTeamDropped, setOtherTeamDropped] = useState<TeamPlayerInfo[]>([]);
	const [selectedTeam, setSelectedTeam] = useState<TeamLeagueTeamsInfo>();
	const queryClient = useQueryClient();

	const tradePlayers = async () => {
		try {
			const response = await tradeTeamLeaguePlayersController(
				store.organizationUuid,
				store.teamLeagueUuid,
				params.teamId,
				currentTeamDropped.map((player) => player.uuid),
				otherTeamDropped.map((player) => player.uuid),
				selectedTeam?.uuid!
			);
			if (response.statusCode === 200) {
				queryClient.invalidateQueries({
					queryKey: ['TEAM_LEAGUE_TEAM_PLAYERS']
				});
				toast.success('Players traded with other team successfully');
				router.push(`/team-leagues/${params.teamLeagueId}/teams/${params.teamId}/player-moves`);
			} else {
				toast.error(response.error);
			}
		} catch (error) {
			toast.error('Something went wrong and we were unable to trade players.');
		}
	};

	const handleTeamSelection = (team: TeamLeagueTeamsInfo | undefined) => {
		setSelectedTeam(team);
		setOtherTeamDropped([]);
	};

	const handleNext = () => {
		if (step === 1) setStep(2);
		else if (step === 2) setStep(3);
		else if (step === 3) setStep(4);
		else if (step === 4) tradePlayers();
	};

	const handleBack = () => {
		if (step === 4) setStep(3);
		else if (step === 3) setStep(2);
		else if (step === 2) setStep(1);
		else router.back();
	};

	return (
		<div className="mx-auto max-w-screen-xl">
			<div className="border-b px-4 py-6 md:px-8">
				<h1 className="text-xl font-semibold">
					<Link href="" onClick={() => router.back()}>
						<FontAwesomeIcon icon={faChevronLeft} size="sm" className="mr-4" />
						Back
					</Link>
				</h1>
			</div>

			<TeamHeader imageUrl={currentTeam?.logo?.url} teamName={currentTeam?.title} />

			<div className="mx-auto hidden max-w-[720px] items-center justify-between px-8 pb-8 pt-4 transition-all md:mb-8 md:px-4 md:pb-0 md:pt-4 lg:flex">
				<Step text="Pick player(s) from your team" active={step === 1} finished={step > 1} onClick={() => setStep(1)} isOrange={true} />
				<Path active={step > 1} isOrange={true} />
				<Step
					disabled={currentTeamDropped.length === 0}
					text="Pick team"
					active={step === 2}
					finished={step > 2}
					onClick={() => setStep(2)}
					isOrange={true}
				/>
				<Path active={step > 2} isOrange={true} />
				<Step
					disabled={!selectedTeam}
					text="Pick player(s) from other team"
					active={step === 3}
					finished={step > 3}
					onClick={() => setStep(3)}
					isOrange={true}
				/>
				<Path active={step > 3} isOrange={true} />
				<Step
					disabled={!selectedTeam}
					text="Review trade"
					active={step === 4}
					finished={step > 4}
					onClick={() => setStep(4)}
					isOrange={true}
				/>
			</div>
			{step === 4 ? (
				<StepFour
					otherTeamDroppedPlayers={otherTeamDropped}
					currentTeamDroppedPlayers={currentTeamDropped}
					currentTeam={currentTeam}
					selectedTeam={selectedTeam}
				/>
			) : (
				<div className="flex flex-col gap-4 p-4 text-darkorange-600 md:p-8">
					{step === 1 && (
						<div className="flex flex-col gap-6 font-medium">
							<p className="text-darkorange-600">
								<span className="lg:hidden">Step 1:</span> To initiate a trade, select a player(s) from your team.{' '}
							</p>
							<p className="hidden lg:block text-darkorange-600">
								Drag the players that would like to trade into the box on the right. You can always drag a player back into the team
								group if there is an error.
							</p>
						</div>
					)}
					<div className="flex justify-between gap-9">
						{step === 1 && (
							<StepOne
								droppedPlayers={currentTeamDropped}
								availablePlayers={currentTeamPlayers}
								setDroppedPlayers={setCurrentTeamDropped}
								setAvailablePlayers={setCurrentTeamPlayers}
							/>
						)}
						{step === 2 && (
							<StepTwo teams={teams} setSelectedTeam={handleTeamSelection} selectedTeam={selectedTeam} teamId={params.teamId} />
						)}
						{step === 3 && selectedTeam && (
							<StepThree selectedTeam={selectedTeam} otherTeamDropped={otherTeamDropped} setOtherTeamDropped={setOtherTeamDropped} />
						)}
						{(currentTeamDropped.length !== 0 || otherTeamDropped.length !== 0) && (
							<TradePreview
								currentTeamDropped={currentTeamDropped}
								otherTeamDropped={otherTeamDropped}
								currentTeam={currentTeam}
								selectedTeam={selectedTeam}
								goToPreview={() => setStep(4)}
							/>
						)}
					</div>
				</div>
			)}
			<div className="flex gap-2 p-4  md:p-8">
				<Button variant="secondary" type="button" size="xs" className="w-fit" onClick={handleBack}>
					<p className="text-xs font-semibold">Back</p>
				</Button>
				<Button
					disabled={(step === 1 && currentTeamDropped.length === 0) || (step === 2 && !selectedTeam)}
					type="button"
					size="xs"
					className="w-fit !text-darkorange-600 !bg-darkorange-100 disabled:!text-darkorange-400 disabled:!bg-darkorange-50 buttonStyle"
					onClick={handleNext}
				>
					<p className="text-xs font-semibold">{step === 4 ? 'Confirm' : 'Next'}</p>
				</Button>
			</div>
		</div>
	);
}
